import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import secretaryService from "services/secretary.js";
import taskService from "services/tasks.js";
import projectService from "services/projects.js";
import logger from "utils/logger";

// Steps of the secretary flow
const STEP = {
  RECORD: "record",
  PROCESSING: "processing",
  PREVIEW: "preview",
  SAVING: "saving",
  SUCCESS: "success",
  ERROR: "error",
};

export default function CardSecretary({ isOpen, onClose, onTaskCreated }) {
  const [step, setStep] = useState(STEP.RECORD);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcription, setTranscription] = useState("");
  const [taskData, setTaskData] = useState(null);
  const [costInfo, setCostInfo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Load projects on open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      resetState();
      loadProjects();
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
      stopRecording();
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetState = () => {
    setStep(STEP.RECORD);
    setIsRecording(false);
    setRecordingTime(0);
    setAudioBlob(null);
    setTranscription("");
    setTaskData(null);
    setCostInfo(null);
    setError("");
    setEditMode(false);
    audioChunksRef.current = [];
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response.data || response.projects || []);
    } catch (err) {
      logger.error("Error loading projects:", err);
    }
  };

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Determine supported MIME type
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4")
        ? "audio/mp4"
        : "audio/ogg";

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);

        // Stop all tracks
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => track.stop());
          streamRef.current = null;
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      logger.error("Error accessing microphone:", err);
      if (err.name === "NotAllowedError") {
        setError(
          "Permiso de micrófono denegado. Por favor, permite el acceso al micrófono en la configuración de tu navegador."
        );
      } else if (err.name === "NotFoundError") {
        setError(
          "No se encontró un micrófono. Asegúrate de que tu dispositivo tenga un micrófono conectado."
        );
      } else {
        setError("Error al acceder al micrófono: " + err.message);
      }
      setStep(STEP.ERROR);
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setIsRecording(false);
  }, []);

  const handleStopAndProcess = useCallback(() => {
    stopRecording();
    // Wait a bit for the blob to be ready from the onstop event
    setTimeout(() => {
      processAudio();
    }, 500);
  }, [stopRecording]); // eslint-disable-line react-hooks/exhaustive-deps

  const processAudio = async () => {
    // audioBlob may not be set yet (onstop is async), so we build it manually
    const mimeType = mediaRecorderRef.current?.mimeType || "audio/webm";
    const blob = new Blob(audioChunksRef.current, { type: mimeType });

    if (!blob || blob.size === 0) {
      setError("No se grabó audio. Intenta de nuevo.");
      setStep(STEP.ERROR);
      return;
    }

    setStep(STEP.PROCESSING);

    try {
      const result = await secretaryService.processAudio(blob, mimeType);
      setTranscription(result.transcription);
      setTaskData({ ...result.task, status: "pending" });
      setCostInfo(result.cost || null);
      setStep(STEP.PREVIEW);
    } catch (err) {
      logger.error("Error processing audio:", err);
      const errorCode = err.response?.data?.error_code;
      const errorMsg =
        errorCode === "insufficient_credits"
          ? "No tienes créditos suficientes para usar Secretary. Ve a la sección de Créditos para recargar tu cuenta."
          : err.response?.data?.error ||
            "Error al procesar el audio. Intenta de nuevo.";
      setError(errorMsg);
      setStep(STEP.ERROR);
    }
  };

  const handleSaveTask = async () => {
    if (!taskData) return;

    setStep(STEP.SAVING);

    try {
      const result = await taskService.createTask({
        title: taskData.title,
        description: taskData.description,
        priority: taskData.priority,
        project_code: taskData.project_code,
        status: taskData.status || "pending",
      });

      setStep(STEP.SUCCESS);

      // Notify parent after a short delay so user sees success state
      setTimeout(() => {
        if (onTaskCreated) {
          onTaskCreated(result.task || result);
        }
      }, 1500);
    } catch (err) {
      logger.error("Error creating task:", err);
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.messages?.title?.[0] ||
        "Error al crear la tarea.";
      setError(errorMsg);
      setStep(STEP.ERROR);
    }
  };

  const handleRetry = () => {
    resetState();
  };

  const handleEditField = (field, value) => {
    setTaskData((prev) => ({ ...prev, [field]: value }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getProjectName = (code) => {
    const project = projects.find((p) => p.project_code === code);
    return project ? project.project_name : code;
  };

  const priorityLabels = {
    low: "Baja",
    medium: "Media",
    high: "Alta",
    urgent: "Urgente",
  };

  const priorityColors = {
    low: "#3b82f6",
    medium: "#eab308",
    high: "#f97316",
    urgent: "#ef4444",
  };

  const statusLabels = {
    created: "Creada",
    pending: "Pendiente",
    working: "En Curso",
    reviewing: "En Revisión",
    finished: "Finalizada",
    failed: "Fallida",
  };

  const statusColors = {
    created: "#9ca3af",
    pending: "#6366f1",
    working: "#f59e0b",
    reviewing: "#3b82f6",
    finished: "#10b981",
    failed: "#ef4444",
  };

  if (!isOpen) return null;

  const renderContent = () => {
    switch (step) {
      case STEP.RECORD:
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "2rem 1rem",
              gap: "1.5rem",
            }}
          >
            <div
              style={{
                fontSize: "0.875rem",
                color: "#6b7280",
                textAlign: "center",
                maxWidth: "400px",
              }}
            >
              Presiona el botón para grabar tu mensaje de voz. Describe la tarea
              que deseas crear, incluyendo el proyecto si es posible.
            </div>

            {/* Recording visualization */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1rem",
              }}
            >
              {isRecording && (
                <div
                  style={{
                    fontSize: "2rem",
                    fontWeight: "700",
                    fontFamily: "monospace",
                    color: "#ef4444",
                  }}
                >
                  {formatTime(recordingTime)}
                </div>
              )}

              <button
                type="button"
                onClick={isRecording ? handleStopAndProcess : startRecording}
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isRecording ? "#ef4444" : "#6366f1",
                  color: "white",
                  fontSize: "2rem",
                  boxShadow: isRecording
                    ? "0 0 0 8px rgba(239,68,68,0.2)"
                    : "0 4px 14px rgba(99,102,241,0.4)",
                  transition: "all 0.3s ease",
                  animation: isRecording ? "pulse 1.5s infinite" : "none",
                }}
              >
                <i
                  className={
                    isRecording ? "fas fa-stop" : "fas fa-microphone"
                  }
                ></i>
              </button>

              <div
                style={{
                  fontSize: "0.75rem",
                  color: isRecording ? "#ef4444" : "#9ca3af",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                {isRecording ? "Grabando... toca para detener" : "Toca para grabar"}
              </div>
            </div>
          </div>
        );

      case STEP.PROCESSING:
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "3rem 1rem",
              gap: "1.5rem",
            }}
          >
            <div className="animate-spin" style={{
              width: "48px",
              height: "48px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #6366f1",
              borderRadius: "50%",
            }}></div>
            <div style={{ fontSize: "1rem", color: "#374151", fontWeight: "600" }}>
              Procesando audio...
            </div>
            <div style={{ fontSize: "0.875rem", color: "#6b7280", textAlign: "center" }}>
              Transcribiendo y generando la tarea con IA. Esto puede demorar unos segundos.
            </div>
          </div>
        );

      case STEP.PREVIEW:
        return (
          <div style={{ padding: "1rem" }}>
            {/* Transcription */}
            <div style={{
              marginBottom: "1.5rem",
              padding: "0.75rem",
              backgroundColor: "#f3f4f6",
              borderRadius: "0.5rem",
              borderLeft: "4px solid #6366f1",
            }}>
              <div style={{
                fontSize: "0.75rem",
                fontWeight: "600",
                color: "#6366f1",
                textTransform: "uppercase",
                marginBottom: "0.25rem",
              }}>
                <i className="fas fa-quote-left" style={{ marginRight: "0.25rem" }}></i>
                Transcripción
              </div>
              <div style={{ fontSize: "0.875rem", color: "#374151", fontStyle: "italic" }}>
                "{transcription}"
              </div>
            </div>

            {/* Credit cost indicator */}
            {(() => {
              const cost = costInfo?.amount ?? 100;
              const balanceBefore = costInfo?.balance_before;
              const balanceAfter = costInfo?.balance_after;
              const hasBalanceInfo = balanceBefore !== null && balanceBefore !== undefined;

              // Color coding based on remaining balance
              let statusColor = "#059669"; // green - safe
              let statusBg = "#d1fae5";
              let statusIcon = "fas fa-check-circle";
              let statusText = "Saldo suficiente";

              if (hasBalanceInfo) {
                if (balanceAfter !== null && balanceAfter <= 0) {
                  statusColor = "#dc2626"; // red - insufficient after
                  statusBg = "#fee2e2";
                  statusIcon = "fas fa-exclamation-circle";
                  statusText = "Saldo agotado tras el uso";
                } else if (balanceBefore !== null && balanceAfter !== null && balanceAfter < balanceBefore * 0.2) {
                  statusColor = "#d97706"; // orange - low
                  statusBg = "#fef3c7";
                  statusIcon = "fas fa-exclamation-triangle";
                  statusText = "Saldo bajo";
                }
              }

              return (
                <div style={{
                  marginBottom: "1.5rem",
                  padding: "0.75rem 1rem",
                  backgroundColor: statusBg,
                  borderRadius: "0.5rem",
                  border: `1px solid ${statusColor}30`,
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "0.5rem",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                      <i className="fas fa-coins" style={{ color: statusColor, fontSize: "1rem" }}></i>
                      <span style={{ fontSize: "0.875rem", fontWeight: "700", color: "#111827" }}>
                        Costo de transcripción
                      </span>
                    </div>
                    <span style={{
                      fontSize: "1rem",
                      fontWeight: "700",
                      color: statusColor,
                    }}>
                      {cost} créditos
                    </span>
                  </div>

                  {hasBalanceInfo && (
                    <div style={{
                      marginTop: "0.5rem",
                      display: "flex",
                      gap: "1rem",
                      flexWrap: "wrap",
                      fontSize: "0.75rem",
                      color: "#6b7280",
                    }}>
                      <span>
                        <span style={{ fontWeight: "600" }}>Saldo anterior:</span> {balanceBefore} créditos
                      </span>
                      <span>
                        <span style={{ fontWeight: "600" }}>Saldo restante:</span>{" "}
                        <span style={{ color: statusColor, fontWeight: "700" }}>{balanceAfter} créditos</span>
                      </span>
                    </div>
                  )}

                  <div style={{
                    marginTop: "0.375rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    fontSize: "0.75rem",
                    color: statusColor,
                    fontWeight: "600",
                  }}>
                    <i className={statusIcon} style={{ fontSize: "0.75rem" }}></i>
                    {statusText}
                  </div>
                </div>
              );
            })()}

            {/* Task preview / edit form */}
            <div style={{
              border: "1px solid #e5e7eb",
              borderRadius: "0.5rem",
              padding: "1rem",
            }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "1rem",
              }}>
                <div style={{
                  fontSize: "0.875rem",
                  fontWeight: "700",
                  color: "#111827",
                }}>
                  <i className="fas fa-tasks" style={{ marginRight: "0.5rem", color: "#6366f1" }}></i>
                  Tarea generada
                </div>
                <button
                  type="button"
                  onClick={() => setEditMode(!editMode)}
                  style={{
                    fontSize: "0.75rem",
                    color: "#6366f1",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: "600",
                  }}
                >
                  <i className={editMode ? "fas fa-eye" : "fas fa-edit"} style={{ marginRight: "0.25rem" }}></i>
                  {editMode ? "Vista previa" : "Editar"}
                </button>
              </div>

              {editMode ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {/* Title */}
                  <div>
                    <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#374151", display: "block", marginBottom: "0.25rem" }}>
                      Título
                    </label>
                    <input
                      type="text"
                      value={taskData?.title || ""}
                      onChange={(e) => handleEditField("title", e.target.value)}
                      maxLength={255}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#374151", display: "block", marginBottom: "0.25rem" }}>
                      Descripción
                    </label>
                    <textarea
                      value={taskData?.description || ""}
                      onChange={(e) => handleEditField("description", e.target.value)}
                      rows={6}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        outline: "none",
                        resize: "vertical",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Priority */}
                  <div>
                    <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#374151", display: "block", marginBottom: "0.25rem" }}>
                      Prioridad
                    </label>
                    <select
                      value={taskData?.priority || "medium"}
                      onChange={(e) => handleEditField("priority", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        outline: "none",
                      }}
                    >
                      <option value="low">Baja</option>
                      <option value="medium">Media</option>
                      <option value="high">Alta</option>
                      <option value="urgent">Urgente</option>
                    </select>
                  </div>

                  {/* Status */}
                  <div>
                    <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#374151", display: "block", marginBottom: "0.25rem" }}>
                      Estado
                    </label>
                    <select
                      value={taskData?.status || "pending"}
                      onChange={(e) => handleEditField("status", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        outline: "none",
                      }}
                    >
                      <option value="created">Creada</option>
                      <option value="pending">Pendiente</option>
                      <option value="working">En Curso</option>
                      <option value="reviewing">En Revisión</option>
                      <option value="finished">Finalizada</option>
                      <option value="failed">Fallida</option>
                    </select>
                  </div>

                  {/* Project */}
                  <div>
                    <label style={{ fontSize: "0.75rem", fontWeight: "600", color: "#374151", display: "block", marginBottom: "0.25rem" }}>
                      Proyecto
                    </label>
                    <select
                      value={taskData?.project_code || ""}
                      onChange={(e) => handleEditField("project_code", e.target.value)}
                      style={{
                        width: "100%",
                        padding: "0.5rem",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.375rem",
                        fontSize: "0.875rem",
                        outline: "none",
                      }}
                    >
                      {projects.map((project) => (
                        <option key={project.project_code} value={project.project_code}>
                          {project.project_name} ({project.project_code})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                  {/* Title display */}
                  <div>
                    <div style={{ fontSize: "1.125rem", fontWeight: "700", color: "#111827" }}>
                      {taskData?.title}
                    </div>
                  </div>

                  {/* Badges row */}
                  <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      color: "white",
                      backgroundColor: priorityColors[taskData?.priority] || "#eab308",
                    }}>
                      {priorityLabels[taskData?.priority] || "Media"}
                    </span>
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      color: "white",
                      backgroundColor: statusColors[taskData?.status] || "#6366f1",
                    }}>
                      {statusLabels[taskData?.status] || "Pendiente"}
                    </span>
                    <span style={{
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "0.125rem 0.5rem",
                      borderRadius: "0.375rem",
                      fontSize: "0.75rem",
                      fontWeight: "600",
                      backgroundColor: "#dbeafe",
                      color: "#1e40af",
                    }}>
                      {getProjectName(taskData?.project_code)}
                    </span>
                  </div>

                  {/* Description display */}
                  <div style={{
                    fontSize: "0.875rem",
                    color: "#374151",
                    lineHeight: "1.625",
                    whiteSpace: "pre-wrap",
                  }}>
                    {taskData?.description}
                  </div>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.5rem",
              marginTop: "1.5rem",
            }}>
              <button
                type="button"
                onClick={handleRetry}
                style={{
                  padding: "0.5rem 1rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <i className="fas fa-redo" style={{ marginRight: "0.375rem" }}></i>
                Grabar de nuevo
              </button>
              <button
                type="button"
                onClick={handleSaveTask}
                style={{
                  padding: "0.5rem 1.25rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  border: "none",
                  backgroundColor: "#6366f1",
                  color: "white",
                  cursor: "pointer",
                }}
              >
                <i className="fas fa-check" style={{ marginRight: "0.375rem" }}></i>
                Crear tarea
              </button>
            </div>
          </div>
        );

      case STEP.SAVING:
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "3rem 1rem",
              gap: "1rem",
            }}
          >
            <div className="animate-spin" style={{
              width: "40px",
              height: "40px",
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #6366f1",
              borderRadius: "50%",
            }}></div>
            <div style={{ fontSize: "1rem", color: "#374151", fontWeight: "600" }}>
              Creando tarea...
            </div>
          </div>
        );

      case STEP.SUCCESS:
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "3rem 1rem",
              gap: "1rem",
            }}
          >
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#d1fae5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <i className="fas fa-check" style={{ fontSize: "1.5rem", color: "#059669" }}></i>
            </div>
            <div style={{ fontSize: "1.125rem", color: "#059669", fontWeight: "700" }}>
              Tarea creada con éxito
            </div>
            <div style={{ fontSize: "0.875rem", color: "#6b7280" }}>
              {taskData?.title}
            </div>
          </div>
        );

      case STEP.ERROR:
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: "2rem 1rem",
              gap: "1rem",
            }}
          >
            <div style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              backgroundColor: "#fee2e2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <i className="fas fa-exclamation-triangle" style={{ fontSize: "1.5rem", color: "#dc2626" }}></i>
            </div>
            <div style={{ fontSize: "1rem", color: "#dc2626", fontWeight: "600" }}>
              Error
            </div>
            <div style={{
              fontSize: "0.875rem",
              color: "#6b7280",
              textAlign: "center",
              maxWidth: "400px",
            }}>
              {error}
            </div>
            <button
              type="button"
              onClick={handleRetry}
              style={{
                marginTop: "0.5rem",
                padding: "0.5rem 1.25rem",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
                fontWeight: "600",
                border: "none",
                backgroundColor: "#6366f1",
                color: "white",
                cursor: "pointer",
              }}
            >
              <i className="fas fa-redo" style={{ marginRight: "0.375rem" }}></i>
              Intentar de nuevo
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && step !== STEP.PROCESSING && step !== STEP.SAVING) {
          stopRecording();
          onClose();
        }
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "0.75rem",
          boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
          width: "100%",
          maxWidth: "540px",
          maxHeight: "90vh",
          overflow: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "1rem 1.25rem",
            borderBottom: "1px solid #e5e7eb",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <i
              className="fas fa-microphone"
              style={{
                color: "#6366f1",
                fontSize: "1.125rem",
              }}
            ></i>
            <h3
              style={{
                fontSize: "1.125rem",
                fontWeight: "700",
                color: "#111827",
                margin: 0,
              }}
            >
              Secretary
            </h3>
          </div>
          {step !== STEP.PROCESSING && step !== STEP.SAVING && (
            <button
              type="button"
              onClick={() => {
                stopRecording();
                onClose();
              }}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#9ca3af",
                fontSize: "1.25rem",
                padding: "0.25rem",
              }}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        {/* Content */}
        {renderContent()}
      </div>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
          70% { box-shadow: 0 0 0 12px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>,
    document.body
  );
}

CardSecretary.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTaskCreated: PropTypes.func,
};
